import { useState } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import Header from "../../../../components/Header";
import useAuth from "../../../../auth/useAuth";
import { useFetchGET } from "../../../../state/FetchHelpers";
import {
  anillosListRoute,
  anilloCiudadesRoute,
  createAnillo,
  updateAnillo,
  addCiudadToAnillo,
  deleteCiudadFromAnillo,
} from "../../../../config";
import { Alerts } from "../../../../components/alerts/Alerts.tsx";
import Loading from "../../../global/Loading";
import FatalError from "../../../global/FatalError";
import ConfirmDialog from "../../../dialogs/confirm/ConfirmDialog";

const AnilloCard = ({ anillo, onAnilloUpdated }) => {
  const auth = useAuth();
  const token = auth.globals.token;
  const { alertSuccess, showErrorRequest } = Alerts();

  const [expanded, setExpanded] = useState(false);
  const [cityRefresh, setCityRefresh] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [addCiudadOpen, setAddCiudadOpen] = useState(false);
  const [confirmDeleteZip, setConfirmDeleteZip] = useState(null);
  const [editName, setEditName] = useState(anillo.name);
  const [editMaxOrders, setEditMaxOrders] = useState(anillo.max_orders);
  const [newCiudad, setNewCiudad] = useState({ city_name: "", state_name: "", zip_code: "" });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const { data: ciudadesData, loading: ciudadesLoading } = useFetchGET(
    expanded ? anilloCiudadesRoute(anillo.id) : undefined,
    token,
    [],
    cityRefresh
  );
  const ciudades = Array.isArray(ciudadesData?.ciudades) ? ciudadesData.ciudades : [];

  const handleEdit = async () => {
    try {
      setSaving(true);
      const resp = await updateAnillo(token, anillo.id, {
        name: editName,
        max_orders: parseInt(editMaxOrders),
      });
      if (resp.ok) {
        alertSuccess("Éxito", "Anillo actualizado");
        setEditOpen(false);
        onAnilloUpdated();
      } else {
        const err = await resp.json().catch(() => null);
        showErrorRequest(err?.detail || err?.message || "No fue posible actualizar el anillo", auth);
      }
    } catch (e) {
      showErrorRequest(e.message || "Error de conexión", auth);
    } finally {
      setSaving(false);
    }
  };

  const handleAddCiudad = async () => {
    try {
      setSaving(true);
      const resp = await addCiudadToAnillo(token, anillo.id, newCiudad);
      if (resp.ok) {
        alertSuccess("Éxito", "Ciudad agregada al anillo");
        setAddCiudadOpen(false);
        setNewCiudad({ city_name: "", state_name: "", zip_code: "" });
        setCityRefresh((p) => p + 1);
      } else {
        const err = await resp.json().catch(() => null);
        showErrorRequest(err?.detail || err?.message || "No fue posible agregar la ciudad", auth);
      }
    } catch (e) {
      showErrorRequest(e.message || "Error de conexión", auth);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCiudad = async (zip_code) => {
    try {
      setDeleting(zip_code);
      const resp = await deleteCiudadFromAnillo(token, anillo.id, zip_code);
      if (resp.ok) {
        alertSuccess("Éxito", "Ciudad quitada del anillo");
        setCityRefresh((p) => p + 1);
      } else {
        const err = await resp.json().catch(() => null);
        showErrorRequest(err?.detail || err?.message || "No fue posible quitar la ciudad", auth);
      }
    } catch (e) {
      showErrorRequest(e.message || "Error de conexión", auth);
    } finally {
      setDeleting(null);
      setConfirmDeleteZip(null);
    }
  };

  return (
    <>
      <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)} sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" pr={2}>
            <Typography fontWeight="bold">{anillo.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              Máx. paradas: {anillo.max_orders} · Ciudades: {anillo.total_ciudades}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" gap={1} mb={2} flexWrap="wrap">
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setEditName(anillo.name);
                setEditMaxOrders(anillo.max_orders);
                setEditOpen(true);
              }}
            >
              Editar
            </Button>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              onClick={() => setAddCiudadOpen(true)}
            >
              Agregar ciudad
            </Button>
          </Box>

          {ciudadesLoading ? (
            <CircularProgress size={24} />
          ) : ciudades.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Sin ciudades asignadas.
            </Typography>
          ) : (
            <List dense>
              {ciudades.map((c) => (
                <ListItem
                  key={c.zip_code}
                  divider
                  secondaryAction={
                    <IconButton
                      size="small"
                      color="error"
                      disabled={deleting === c.zip_code}
                      onClick={() => setConfirmDeleteZip(c.zip_code)}
                    >
                      {deleting === c.zip_code ? (
                        <CircularProgress size={16} />
                      ) : (
                        <DeleteIcon fontSize="small" />
                      )}
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={`${c.city_name}, ${c.state_name}`}
                    secondary={`ZIP: ${c.zip_code}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </AccordionDetails>
      </Accordion>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Editar anillo</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}>
          <TextField
            label="Nombre"
            variant="filled"
            size="small"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            fullWidth
          />
          <TextField
            label="Máx. paradas"
            variant="filled"
            size="small"
            type="number"
            value={editMaxOrders}
            onChange={(e) => setEditMaxOrders(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleEdit}
            variant="contained"
            color="secondary"
            disabled={saving || !editName}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addCiudadOpen} onClose={() => setAddCiudadOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Agregar ciudad — {anillo.name}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}>
          <TextField
            label="Ciudad"
            variant="filled"
            size="small"
            value={newCiudad.city_name}
            onChange={(e) => setNewCiudad((p) => ({ ...p, city_name: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Estado"
            variant="filled"
            size="small"
            value={newCiudad.state_name}
            onChange={(e) => setNewCiudad((p) => ({ ...p, state_name: e.target.value }))}
            fullWidth
          />
          <TextField
            label="ZIP"
            variant="filled"
            size="small"
            value={newCiudad.zip_code}
            onChange={(e) => setNewCiudad((p) => ({ ...p, zip_code: e.target.value }))}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCiudadOpen(false)} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleAddCiudad}
            variant="contained"
            color="secondary"
            disabled={saving || !newCiudad.city_name || !newCiudad.zip_code}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : "Agregar"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDeleteZip}
        title="Quitar ciudad"
        message={`¿Confirmas que deseas quitar el ZIP ${confirmDeleteZip} de este anillo?`}
        onClose={(confirmed) => {
          if (confirmed) handleDeleteCiudad(confirmDeleteZip);
          else setConfirmDeleteZip(null);
        }}
      />
    </>
  );
};

const GestionAnillos = () => {
  const auth = useAuth();
  const token = auth.globals.token;
  const { alertSuccess, showErrorRequest } = Alerts();

  const canAccess =
    auth.globals.id_role === 1 || auth.globals.id_role === 29 || auth.globals.id_role === 31;

  const [refreshToggle, setRefreshToggle] = useState(0);
  const [crearOpen, setCrearOpen] = useState(false);
  const [newAnillo, setNewAnillo] = useState({ name: "", max_orders: "" });
  const [saving, setSaving] = useState(false);

  const { data: anillosData, loading, error } = useFetchGET(
    canAccess ? anillosListRoute() : undefined,
    token,
    [],
    refreshToggle
  );
  const anillos = Array.isArray(anillosData?.anillos) ? anillosData.anillos : [];

  const refreshAnillos = () => setRefreshToggle((p) => p + 1);

  const handleCrearAnillo = async () => {
    try {
      setSaving(true);
      const resp = await createAnillo(token, {
        name: newAnillo.name,
        max_orders: parseInt(newAnillo.max_orders),
      });
      if (resp.ok) {
        alertSuccess("Éxito", "Anillo creado correctamente");
        setCrearOpen(false);
        setNewAnillo({ name: "", max_orders: "" });
        refreshAnillos();
      } else {
        const err = await resp.json().catch(() => null);
        showErrorRequest(err?.detail || err?.message || "No fue posible crear el anillo", auth);
      }
    } catch (e) {
      showErrorRequest(e.message || "Error de conexión", auth);
    } finally {
      setSaving(false);
    }
  };

  if (!canAccess) {
    return (
      <Box m="20px" marginBottom="64px">
        <Header title="Gestión de Anillos" subtitle="Acceso restringido" />
        <Alert severity="warning">
          <AlertTitle sx={{ fontWeight: "bold" }}>Sin permisos</AlertTitle>
          No tienes permisos para acceder a esta sección.
        </Alert>
      </Box>
    );
  }

  if (error) return <FatalError />;

  return (
    <Box m="20px" marginBottom="64px">
      <Header title="Gestión de Anillos" subtitle="Administración de anillos de ruteo automático" />

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button variant="contained" color="secondary" onClick={() => setCrearOpen(true)}>
          + Crear anillo nuevo
        </Button>
      </Box>

      {loading ? (
        <Loading />
      ) : anillos.length === 0 ? (
        <Typography align="center" color="text.secondary" mt={4}>
          No hay anillos configurados.
        </Typography>
      ) : (
        anillos.map((anillo) => (
          <AnilloCard key={anillo.id} anillo={anillo} onAnilloUpdated={refreshAnillos} />
        ))
      )}

      <Dialog open={crearOpen} onClose={() => setCrearOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Crear anillo nuevo</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}>
          <TextField
            label="Nombre"
            variant="filled"
            size="small"
            value={newAnillo.name}
            onChange={(e) => setNewAnillo((p) => ({ ...p, name: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Máx. paradas"
            variant="filled"
            size="small"
            type="number"
            value={newAnillo.max_orders}
            onChange={(e) => setNewAnillo((p) => ({ ...p, max_orders: e.target.value }))}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCrearOpen(false)} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleCrearAnillo}
            variant="contained"
            color="secondary"
            disabled={saving || !newAnillo.name || !newAnillo.max_orders}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GestionAnillos;
